import {Avatar, Box, Button, Grid, IconButton, Stack, Typography, useMediaQuery, useTheme} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';
import {IMediaTrack} from '../../../../dto/MediaTrack.dto';
import {WalkupMusicSelectionTable} from '../WalkupMusicSelectionTable';
import {
  AllowedPlayerTypeKeys,
  ListPlayerTypeBased,
} from '../stepperComponents/SongMappingStepperCmpt';
import {INewWalkupPlayer} from '../stepperComponents/WalkupLineupStepper';
import { useDispatch } from 'react-redux';
import { closePlayer } from '../../../../store/Slices/playerSlice';

export interface MobileViewProps {
  isVisible: boolean;
  playerId: number;
  setPlayerId: React.Dispatch<React.SetStateAction<number>>;
  selectedPlayerType: AllowedPlayerTypeKeys;
  setSelectedPlayerType: React.Dispatch<React.SetStateAction<AllowedPlayerTypeKeys>>;
  currentFormStudents: INewWalkupPlayer[];
  selectedPlayer?: INewWalkupPlayer;
  track: IMediaTrack[];
  onSelect: (track: IMediaTrack) => void;
  onDeSelect: (track: IMediaTrack) => void;
  submit: () => void;
  isAthlete: boolean;
  songSelectionRef: React.RefObject<HTMLDivElement>;
}

export function useIsSubmitDisabled(currentFormStudents: INewWalkupPlayer[]): boolean {
  return React.useMemo(() => {
    return !currentFormStudents.some(player =>
      player.player_type?.some(type => {
        const playerType = type.toLowerCase() as AllowedPlayerTypeKeys;
        // Only consider players who had no walkup_track_id on load, but now have a track selected
        return (
          (!player[playerType]?.walkup_track_id) && player[playerType]?.track
        );
      })
    );
  }, [currentFormStudents]);
}

export const WalkupMobileView = React.memo<MobileViewProps>(
  ({
    isVisible,
    playerId,
    setPlayerId,
    selectedPlayerType,
    setSelectedPlayerType,
    currentFormStudents,
    selectedPlayer,
    track,
    onSelect,
    onDeSelect,
    submit,
    isAthlete,
    songSelectionRef,
  }) => {
    const theme = useTheme();
    const isVerySmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const dispatch = useDispatch();
    const isSubmitDisabled = useIsSubmitDisabled(currentFormStudents);

    if (!isVisible) return null;

    return (
      <Stack sx={{height: `calc(100vh - ${theme.spacing(8)})`, display: {xs: 'flex', sm: 'flex', md: 'none'}, overflow: 'hidden', bgcolor: 'background.default'}}>
        <Stack spacing={0} sx={{height: '100%'}}>
          <Box textAlign='center' sx={{py: 1, mb: 0.25}}>
            <Typography
              className='text-center font-bold'
              variant='h5'>
              STUDENT SELECTION FORM
            </Typography>
          </Box>
          <Stack
            spacing={0}
            sx={{
              p: 1,
              maxWidth: 'xl',
              mx: 'auto',
              minHeight: 0,
              height: `calc(100vh - ${theme.spacing(17)})`,
            }}
            className='text-center w-full blue-variant6'
            >
              <Box className='flex blue-variant6' sx={{ width: '100%', justifyContent: 'center', alignItems: 'center', py: 2}}>
                <Typography variant='h6' className='font-bold' sx={{py: 0, my: 0}}>
                  Select a player to assign a song
                </Typography>
              </Box>
              <Box sx={{flex: 1, flexDirection: 'row', minHeight: 0, overflow: 'hidden'}}>
                <Grid container sx={{height: `calc(100vh - ${theme.spacing(8)})`}}>
                {!playerId && (
                  <Grid
                    item
                    xs
                    className='playerList blue_variant36'
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                    }}>
                    <Box
                      className='flex-column'
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        maxHeight: theme => `calc(100vh - ${theme.spacing(31)})`,
                      }}>
                      {isAthlete ? (
                        <Stack spacing={2} sx={{height: '100%', gap: 1}}>
                          <ListPlayerTypeBased
                            selectedPlayerType={selectedPlayerType}
                            setSelectedPlayerType={setSelectedPlayerType}
                            playerId={playerId}
                            setPlayerId={setPlayerId}
                            players={currentFormStudents}
                            playerType='athlete'
                            isStudentForm={true}
                          />
                        </Stack>
                      ) : (
                          <Stack spacing={2} sx={{height: '100%', gap: 1}}>
                            <ListPlayerTypeBased
                            selectedPlayerType={selectedPlayerType}
                            setSelectedPlayerType={setSelectedPlayerType}
                            playerId={playerId}
                            setPlayerId={setPlayerId}
                            players={currentFormStudents}
                            playerType='hitter'
                            isStudentForm={true}
                            />
                            <ListPlayerTypeBased
                              selectedPlayerType={selectedPlayerType}
                              setSelectedPlayerType={setSelectedPlayerType}
                              playerId={playerId}
                              setPlayerId={setPlayerId}
                              players={currentFormStudents}
                              playerType='pitcher'
                              isStudentForm={true}
                            />
                          </Stack>
                      )}
                    </Box>
                  </Grid>
                )}
                {playerId !== 0 && (
                  <Grid
                    item
                    xs
                    className='walkupTrackSelection calcheight-52 scrollbar overflow-auto'
                    sx={{overflow: 'visible'}}>
                    <Box
                      className='blue-variant6'
                      ref={songSelectionRef}
                      sx={{
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '90%',
                        height: `calc(100vh - ${theme.spacing(9)})`,
                        p: 1,
                        borderRadius: '1%',
                        zIndex: theme.zIndex.modal,
                        boxShadow: 24,
                        display: 'flex',
                        flexDirection: 'column',
                      }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 1,
                          flexShrink: 0,
                        }}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                          {selectedPlayer ? <Avatar
                            alt={`${selectedPlayer.first_name.charAt(0)}`}
                            src={'/static/images/avatar/1.jpg'}
                            className='avatar bg-gray-variant2'
                            /> : null}
                          <Typography sx={{fontWeight: 'bold'}}>
                            {selectedPlayer
                              ? `#${selectedPlayer.player_no} - ${selectedPlayer.first_name} ${selectedPlayer.last_name} (${selectedPlayerType.charAt(0).toUpperCase() + selectedPlayerType.slice(1)})`
                              : 'Select a Player'}
                          </Typography>
                        </Box>
                        <IconButton
                          aria-label='Close player selection dialog'
                          onClick={() => setPlayerId(0)}
                          sx={{
                            color: 'white',
                            '&:hover': {bgcolor: 'rgba(255, 255, 255, 0.1)'},
                          }}>
                          <CloseIcon onClick={() => {
                            dispatch(closePlayer()); // Stop Redux player from playing the track preview when the player selection dialog is closed
                            setPlayerId(0);
                          }} />
                        </IconButton>
                      </Box>
                      <Box sx={{flex: 1, overflow: 'auto', minHeight: 0, pb: isVerySmallScreen ? 18 : 0}}>
                        <WalkupMusicSelectionTable
                          selectedTracks={track}
                          onSelect={onSelect}
                          onDeselect={onDeSelect}
                          isAddDisabled={playerId === 0}
                        />
                      </Box>
                      <Stack
                        className='flex justify-center items-center blue-variant6'
                        direction='row'
                        spacing={1}
                        sx={{
                          px: 2,
                          zIndex: theme.zIndex.modal,
                        }}>
                        <Button
                          variant='contained'
                          onClick={() => {
                            dispatch(closePlayer()); // Stop Redux player from playing the track preview when the player selection dialog is closed
                            setPlayerId(0);
                          }}
                          disabled={
                            !selectedPlayer?.[selectedPlayerType]?.track &&
                            !selectedPlayer?.[selectedPlayerType]?.walkup_track_id
                          }>
                          Select
                        </Button>
                      </Stack>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Stack>
          {!playerId && (
            <Box sx={{minHeight: 0, overflow: 'hidden'}}>
              <Stack
                className='flex justify-center items-center'
                direction='row'
                spacing={2}
                sx={{
                  p: 2,
                }}>
                <Button variant='contained' onClick={submit} disabled={isSubmitDisabled}>
                  Submit
                </Button>
              </Stack>
            </Box>
          )}
        </Stack>
      </Stack>
    );
  },
);


